/**
 * useCMSPage - Hook to fetch CMS page content by slug
 * Used by public-facing pages to render dynamic CMS content
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useCMSPage = (slug) => {
  const [pageData, setPageData] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPageContent = useCallback(async () => {
    if (!slug) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First get all pages to find the one with matching slug
      const pagesResponse = await axios.get(`${API_URL}/api/cms/pages`);
      const pages = pagesResponse.data;
      const page = pages.find(p => p.slug === slug);
      
      if (!page) {
        setError('Page not found');
        setLoading(false);
        return;
      }

      // Get full page details with sections
      const pageResponse = await axios.get(`${API_URL}/api/cms/pages/by-id/${page.id}`);
      const fullPage = pageResponse.data;
      
      setPageData(fullPage);
      setSections(fullPage.sections || []);
    } catch (err) {
      console.error('Error fetching CMS page:', err);
      setError(err.message || 'Failed to fetch page content');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPageContent();
  }, [fetchPageContent]);

  // Helper to get a specific section by identifier
  const getSection = useCallback((identifier) => {
    return sections.find(s => s.identifier === identifier);
  }, [sections]);

  // Helper to get blocks from a section
  const getSectionBlocks = useCallback((identifier) => {
    const section = getSection(identifier);
    return section?.blocks || [];
  }, [getSection]);

  // Helper to get first block content from a section
  const getSectionContent = useCallback((identifier) => {
    const blocks = getSectionBlocks(identifier);
    return blocks.length > 0 ? blocks[0].content : null;
  }, [getSectionBlocks]);

  // Helper to check if section is visible
  const isSectionVisible = useCallback((identifier) => {
    const section = getSection(identifier);
    return section?.isVisible !== false;
  }, [getSection]);

  return {
    pageData,
    sections,
    loading,
    error,
    refetch: fetchPageContent,
    getSection,
    getSectionBlocks,
    getSectionContent,
    isSectionVisible
  };
};

export default useCMSPage;
